import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import NeuroPrintSummary from '../components/NeuroPrintSummary.js';
import { useNavigate } from 'react-router-dom';

import CLINICAL_DATABASE from './neuro/data/clinicalDatabase.js';
import SUBJECTIVE_DB from './neuro/data/subjectiveDatabase.js';
import { isNormalOpt, STEPS, NEURO_DRAFT_KEY, SAVED_NEURO_ASSESSMENTS_KEY, API_BASE_URL, MAX_CHAT_HISTORY, theme, styles, DEFAULT_FORM_DATA } from './neuro/constants.js';

import SensoryAssessment from './neuro/assessments/SensoryAssessment.js';
import CranialNerveAssessment from './neuro/assessments/CranialNerveAssessment.js';
import ReflexAssessment from './neuro/assessments/ReflexAssessment.js';
import CoordinationAssessment from './neuro/assessments/CoordinationAssessment.js';
import BalanceAssessment from './neuro/assessments/BalanceAssessment.js';
import PostureAssessment from './neuro/assessments/PostureAssessment.js';
import GaitAnalysisAssessment from './neuro/assessments/GaitAnalysisAssessment.js';
import HandFunctionAssessment from './neuro/assessments/HandFunctionAssessment.js';
import SystemsReviewAssessment from './neuro/assessments/SystemsReviewAssessment.js';
import VoluntaryControlAssessment from './neuro/assessments/VoluntaryControlAssessment.js';
import ROMAssessment from './neuro/assessments/ROMAssessment.js';
import FIMAssessment from './neuro/assessments/FIMAssessment.js';
import MMSEScorer from './neuro/assessments/MMSEScorer.js';
import ExpandableMuscleTone from './neuro/assessments/MuscleToneAssessment.js';

import CollapsibleSectionAssessment from './neuro/components/CollapsibleSectionAssessment.js';
import SubjectiveSection from './neuro/components/SubjectiveSection.js';
import SmartRow from './neuro/components/SmartRow.js';
import { getAuthHeaders, userKey } from '../utils/auth.js';
import { pushAssessments } from '../utils/syncAssessments.js';
import { payForDiagnosis } from '../utils/payDiagnosis.js';

const UnifiedNeuroForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [assessmentId, setAssessmentId] = useState(null);
  const [savedAssessments, setSavedAssessments] = useState([]);
  const [activePattern, setActivePattern] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [payGate, setPayGate] = useState(null);
  const [payGateError, setPayGateError] = useState(null);
  const [hoveredZone, setHoveredZone] = useState('');
  const [draftReady, setDraftReady] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [diagnosisData, setDiagnosisData] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagTab, setDiagTab] = useState('ai');
  const [aiAutoFilled, setAiAutoFilled] = useState(false);
  const [validationModal, setValidationModal] = useState(null);

  /* ── Gate: redirect to /home if at limit and no prepaid/reopened draft ── */
  useEffect(() => {
    try {
      const draft = JSON.parse(window.localStorage.getItem(userKey(NEURO_DRAFT_KEY)) || 'null');
      if (draft?.assessmentId) return;
    } catch {}
    fetch(`${API_BASE_URL}/api/user/usage`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u && !u.isAdmin && u.tier === 'free' && u.used >= u.limit) navigate('/home'); })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    try {
      const savedList = window.localStorage.getItem(userKey(SAVED_NEURO_ASSESSMENTS_KEY));
      if (savedList) setSavedAssessments(JSON.parse(savedList));

      const savedDraft = window.localStorage.getItem(userKey(NEURO_DRAFT_KEY));
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        if (typeof parsed.currentStep === 'number' && parsed.currentStep >= 0) setCurrentStep(parsed.currentStep);
        else setCurrentStep(0);

        if (typeof parsed.activePattern === 'string') setActivePattern(parsed.activePattern);
        if (typeof parsed.isSubmitted === 'boolean') setIsSubmitted(parsed.isSubmitted);
        if (Array.isArray(parsed.chatHistory)) setChatHistory(parsed.chatHistory);
        if (typeof parsed.chatInput === 'string') setChatInput(parsed.chatInput);
        if (parsed.assessmentId) setAssessmentId(parsed.assessmentId);
        if (parsed.diagnosisData) setDiagnosisData(parsed.diagnosisData);
        if (parsed.formData && typeof parsed.formData === 'object') {
          setFormData({ ...DEFAULT_FORM_DATA, ...parsed.formData });
        }
      } else {
        setCurrentStep(0);
      }
    } catch (error) {
      console.error('Unable to restore draft:', error);
    } finally {
      setDraftReady(true);
    }
  }, []);

  useEffect(() => {
    if (!draftReady) return;
    try {
      window.localStorage.setItem(userKey(NEURO_DRAFT_KEY), JSON.stringify({
        currentStep, activePattern, isSubmitted, chatHistory, chatInput, formData, assessmentId, diagnosisData
      }));
    } catch (error) {
      console.error('Unable to save draft:', error);
    }
  }, [draftReady, currentStep, activePattern, isSubmitted, chatHistory, chatInput, formData, assessmentId, diagnosisData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (e.target.tagName === 'TEXTAREA' && name === 'hpiText' && value.endsWith('\n') && !value.endsWith('• ')) {
      newValue = value + '• ';
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const addRow = (listName, defaultObj) => setFormData(prev => ({ ...prev, [listName]: [...(prev[listName] || []), { id: Date.now(), ...defaultObj }] }));
  const updateRow = (listName, id, updates) => setFormData(prev => ({ ...prev, [listName]: prev[listName].map(row => row.id === id ? { ...row, ...updates } : row) }));
  const deleteRow = (listName, id) => setFormData(prev => ({ ...prev, [listName]: prev[listName].filter(row => row.id !== id) }));

  const toggleChip = (listName, item) => {
    setFormData(prev => {
      const current = prev[listName] || [];
      return { ...prev, [listName]: current.includes(item) ? current.filter(i => i !== item) : [...current, item] };
    });
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.name.trim()) {
      alert("Patient Name is required before proceeding.");
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const currentStepRef = useRef(currentStep);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  useEffect(() => {
    window.history.pushState(null, '');
    const onPopState = () => {
      if (currentStepRef.current > 0) {
        window.history.pushState(null, '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCurrentStep(s => Math.max(s - 1, 0));
      } else {
        navigate('/home');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigate]);

  const handleSaveAndReturnHome = () => {
    const record = {
      id: assessmentId || Date.now(),
      date: new Date().toLocaleDateString(),
      patientName: formData.name || 'Unnamed Patient',
      pattern: activePattern || 'Unspecified Pattern',
      diagnosis: formData.diagnosis || diagnosisData?.lesionLocalization || 'Pattern not yet analyzed',
      data: formData,
      diagnosisData: diagnosisData,
      isSubmitted,
      chatHistory
    };

    const existingIndex = savedAssessments.findIndex(a => a.id === record.id);
    let updatedList;
    if (existingIndex >= 0) {
      updatedList = [...savedAssessments];
      updatedList[existingIndex] = record;
    } else {
      updatedList = [record, ...savedAssessments];
    }

    setSavedAssessments(updatedList);
    window.localStorage.setItem(userKey(SAVED_NEURO_ASSESSMENTS_KEY), JSON.stringify(updatedList));
    pushAssessments('saved-neuro-assessments', updatedList);

    setFormData(DEFAULT_FORM_DATA);
    setActivePattern(null);
    setIsSubmitted(false);
    setChatHistory([]);
    setAssessmentId(null);
    setDiagnosisData({});
    window.localStorage.removeItem(userKey(NEURO_DRAFT_KEY));

    navigate('/home');
  };

  const handleChatQuery = async () => {
    if (!chatInput.trim()) return;
    setChatLoading(true);
    const userMsg = { role: 'user', text: chatInput };
    const trimmedHistory = [...chatHistory, userMsg].slice(-MAX_CHAT_HISTORY);
    setChatHistory(trimmedHistory);
    setChatInput("");

    try {
      // If a sessionId exists the server already has patternAnalysis cached —
      // only send recent messages (saves ~300-600 input tokens per turn).
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const body = chatSessionId
        ? { messages: trimmedHistory.slice(-5), sessionId: chatSessionId, assessmentId: aid }
        : {
            messages: trimmedHistory.slice(-5),
            assessmentId: aid,
            context: {
              pattern:         activePattern,
              diagnosis:       formData.diagnosis,
              patientName:     formData.name,
              patternAnalysis: diagnosisData,
            }
          };

      const res = await fetch(`${API_BASE_URL}/api/chat-case`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data._sessionExpired) setChatSessionId(null); // session expired — clear so next turn reinitialises
      if (data.sessionId && !chatSessionId) setChatSessionId(data.sessionId);
      setChatHistory(prev => [...prev, { role: 'ai', text: data.reply || data.message || "No response received." }].slice(-MAX_CHAT_HISTORY));
    } catch {
      setChatHistory(prev => [...prev, { role: 'ai', text: "Connection error. Please ensure the backend server is running." }].slice(-MAX_CHAT_HISTORY));
    } finally {
      setChatLoading(false);
    }
  };

  const checkNeuroRequirements = () => {
    const missing = [];
    const nonEmpty = (v) => v && typeof v === 'object' && Object.keys(v).length > 0;
    if (!activePattern?.trim()) missing.push("Neurological pattern must be selected");
    if (!formData.chiefComplaint?.trim()) missing.push("Chief complaint is required");
    const obj = formData;
    const coreCompleted = [obj.mmtState, obj.toneState, obj.reflexData, obj.sensoryData].filter(nonEmpty).length;
    if (coreCompleted < 2)
      missing.push(`At least 2 core assessments required (MMT, Tone, Reflexes, Sensory) — ${coreCompleted} of 4 completed`);
    const hasSupplementary = [obj.cranialData, obj.coordinationData, obj.balanceData, obj.fimData].some(nonEmpty);
    if (!hasSupplementary)
      missing.push("At least one supplementary assessment required: Cranial Nerves, Coordination, Balance, or FIM");
    return missing;
  };

  const handleFinalSubmit = async () => {
    const missing = checkNeuroRequirements();
    if (missing.length > 0) {
      setValidationModal(missing);
      return;
    }
    setIsDiagnosing(true);
    try {
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const res = await fetch(`${API_BASE_URL}/api/analyze-neuro-pattern`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          assessmentId: aid,
          pattern: activePattern,
          diagnosis: formData.diagnosis,
          patientInfo: {
            name: formData.name, age: formData.age, gender: formData.gender,
            side: formData.symSide, onset: formData.symOnset, duration: formData.symDuration,
          },
          subjectiveHistory: {
            chiefComplaint: formData.chiefComplaint,
            hpi: formData.hpiText,
            severity: formData.symSeverity,
            cause: formData.symCause,
            historyMedical: formData.historyMedical,
          },
          objectiveFindings: {
            mmtState: formData.mmtState,
            toneState: formData.toneState,
            reflexData: formData.reflexData,
            sensoryData: formData.sensoryData,
            cranialData: formData.cranialData,
            mmseData: formData.mmseData,
            fimData: formData.fimData,
            gaitData: formData.gaitData,
            balanceData: formData.balanceData,
            coordinationData: formData.coordinationData,
            voluntaryControlData: formData.voluntaryControlData,
            romData: formData.romData,
            postureData: formData.postureData,
            handData: formData.handData,
            systemsData: formData.systemsData,
          },
          investigations: formData.investigation_list,
        })
      });
      const data = await res.json();
      if (res.status === 401) { navigate('/app'); return; }
      if (res.status === 402 && data.pay_required) {
        setPayGate({ assessmentId: data.assessmentId || aid });
        return;
      }
      if (data.status === "insufficient_data") {
        const list = (data.missing || []).map(m => `• ${m}`).join("\n");
        setDiagnosisData({
          error: true,
          lesionLocalization: `Cannot analyse — incomplete assessment:\n\n${list}\n\nPlease complete these sections and retry.`,
        });
      } else if (data.status === "contradictory_data") {
        const list = (data.issues || []).map(i => `• ${i}`).join("\n");
        setDiagnosisData({
          error: true,
          lesionLocalization: `Conflicting neurological findings — please review before analysing:\n\n${list}`,
        });
      } else if (data.status === "partial" || !data.lesionLocalization) {
        setDiagnosisData({
          error: true,
          lesionLocalization: data.message || "Analysis incomplete. Please add more objective findings and retry.",
        });
      } else {
        setDiagnosisData(data);
        setFormData(prev => ({
          ...prev,
          functionalDiagnosis: prev.functionalDiagnosis || data.functionalDiagnosis || data.lesionLocalization || '',
          shortTermGoals: prev.shortTermGoals || (Array.isArray(data.shortTermGoals) ? data.shortTermGoals.join('\n') : ''),
          longTermGoals: prev.longTermGoals || (Array.isArray(data.longTermGoals) ? data.longTermGoals.join('\n') : ''),
          treatmentPlan: prev.treatmentPlan || (Array.isArray(data.rehabPriorities) ? data.rehabPriorities.map(p => typeof p === 'string' ? p : `${p.priority}: ${p.intervention} (${p.frequency})`).join('\n') : ''),
        }));
        setAiAutoFilled(true);
      }
      setIsSubmitted(true);
    } catch {
      setDiagnosisData({
        error: true,
        lesionLocalization: "Unable to connect to server. Please ensure the backend is running.",
      });
      setIsSubmitted(true);
    } finally {
      setIsDiagnosing(false);
    }
  };

  const renderAiNotes = (text = '') => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map((line, index) => <p key={index} style={{ margin: '0 0 8px 0' }}>{line}</p>);
  };

  return (
    <>
    <div style={styles.appWrapper}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        input, select, textarea, button { font-family: inherit; }
        ::placeholder { color: #94a3b8; opacity: 1; }
        button:active { opacity: 0.72; transform: scale(0.96); transition: transform 0.07s, opacity 0.07s !important; }
        select { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 36px !important; }
        @media (max-width: 480px) {
          div[style*="repeat(auto-fit"] { grid-template-columns: 1fr !important; }
          input:not([class]), select { min-height: 48px !important; }
          /* Reference guide scroll fade */
          .neuro-ref-section { position: relative; }
          .neuro-ref-section::after { content: ''; position: absolute; top: 0; right: 0; bottom: 10px; width: 32px; background: linear-gradient(to right, transparent, #f1f5f9); pointer-events: none; border-radius: 0 12px 12px 0; }
        }
        @page { margin: 0.5cm; }
        @media print {
          body > *:not(#neuro-print-summary) { display: none !important; }
          #neuro-print-summary { display: block !important; padding: 24px; background: #fff; font-family: 'Inter', sans-serif; font-size: 11px; color: #0f172a; }
          #neuro-print-summary > * { page-break-inside: avoid; }
        }
        @media screen { #neuro-print-summary { display: none; } }
        @media (max-width: 560px) {
          .neuro-step-label { display: none !important; }
          .neuro-step-node { min-width: 32px !important; }
          .neuro-nav-title { font-size: 0.82rem !important; }
          .neuro-zone-badge { display: none !important; }
          .neuro-content-pad { padding: 0 10px !important; }
        }
        @media (max-width: 480px) {
          .neuro-subj-header  { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .neuro-subj-title   { font-size: 1.1rem !important; }
          .neuro-subj-badges  { justify-content: flex-start !important; }
          .neuro-subj-grid2   { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 375px) {
          .neuro-step-node { min-width: 28px !important; min-height: 28px !important; }
          .neuro-content-pad { padding: 0 8px !important; }
        }
        @media (max-width: 360px) {
          .neuro-step-node { min-width: 26px !important; min-height: 26px !important; font-size: 0.7rem !important; }
          .neuro-nav-title { font-size: 0.75rem !important; }
        }
        @media (max-width: 480px) {
          /* Pattern selector — compact 2-column grid */
          .neuro-pattern-title { font-size: 0.85rem !important; margin-bottom: 8px !important; }
          .neuro-pattern-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 6px !important; padding-bottom: 0 !important; }
          .neuro-pattern-card { flex-direction: row !important; padding: 8px 10px !important; gap: 8px !important; align-items: center !important; border-radius: 12px !important; }
          .neuro-pattern-icon { width: 32px !important; height: 32px !important; min-width: 32px !important; border-radius: 9px !important; font-size: 16px !important; margin-bottom: 0 !important; flex-shrink: 0 !important; }
          .neuro-pattern-name { font-size: 0.78rem !important; line-height: 1.2 !important; }
          .neuro-pattern-region { font-size: 0.65rem !important; }
          /* Reference guide — horizontal scroll so cards stay readable */
          .neuro-ref-section { margin-top: 12px !important; }
          .neuro-ref-label { font-size: 0.65rem !important; margin-bottom: 8px !important; }
          .neuro-ref-grid { display: flex !important; flex-direction: row !important; overflow-x: auto !important; gap: 10px !important; padding-bottom: 10px !important; -webkit-overflow-scrolling: touch; }
          .neuro-ref-card { flex-shrink: 0 !important; width: 160px !important; padding: 10px !important; border-radius: 12px !important; }
          .neuro-ref-card-hdr { margin-bottom: 8px !important; gap: 6px !important; }
          .neuro-ref-card-icon { width: 22px !important; height: 22px !important; min-width: 22px !important; border-radius: 6px !important; font-size: 11px !important; }
          .neuro-ref-card-title { font-size: 0.72rem !important; }
          .neuro-ref-card-sub { font-size: 0.6rem !important; }
          .neuro-ref-group { margin-bottom: 6px !important; }
          .neuro-ref-group-title { font-size: 0.58rem !important; margin-bottom: 2px !important; }
          .neuro-ref-item { font-size: 0.62rem !important; line-height: 1.3 !important; }
          .neuro-goals-row { grid-template-columns: 1fr !important; }
          .neuro-goals-row > div:first-child { border-right: none !important; border-bottom: 1px solid #e2e8f0 !important; text-align: left !important; justify-content: flex-start !important; padding: 10px 14px !important; }
        }
        .ai-card-pre { padding: 20px 0 28px; text-align: center; }
        .ai-pre-title { margin: 0 0 10px; color: #1e40af; font-size: 1.3rem; font-weight: 800; }
        .ai-pre-desc { color: #1d4ed8; margin: 0 0 20px; font-size: 1rem; font-weight: 500; line-height: 1.6; }
        .ai-analyze-btn { padding: 16px 32px; font-size: 1.1rem; }
        .ai-results-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 25px; border-bottom: 1px solid #e2e8f0; padding-bottom: 15px; }
        .ai-grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-bottom: 20px; }
        @media (max-width: 600px) {
          .ai-card-pre { padding: 10px 0 18px; }
          .ai-pre-title { font-size: 1.05rem !important; }
          .ai-pre-desc { font-size: 0.88rem !important; margin-bottom: 14px !important; }
          .ai-analyze-btn { padding: 14px 18px !important; font-size: 0.95rem !important; width: 100% !important; box-sizing: border-box !important; }
          .ai-results-header h3 { font-size: 1.1rem !important; }
          .ai-grid-2 { grid-template-columns: 1fr !important; gap: 10px !important; }
          .diag-field-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .diag-field-row input { width: 100% !important; box-sizing: border-box !important; }
        }
      `}</style>

      {/* ── VALIDATION MODAL ── */}
      {validationModal && (
        <div
          onClick={() => setValidationModal(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '20px', padding: '28px 28px 24px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: 40, height: 40, borderRadius: '12px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>⚠️</div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#0f172a' }}>Incomplete Data</h3>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>Fill in the required fields before running analysis</p>
              </div>
            </div>

            <div style={{ background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8' }}>Required to proceed</p>
              {validationModal.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < validationModal.length - 1 ? '10px' : 0 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', marginTop: '6px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setValidationModal(null)}
                style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Go Back & Fill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PRINT-ONLY SUMMARY ── */}
      {ReactDOM.createPortal(
        <div id="neuro-print-summary">
          <NeuroPrintSummary data={formData} diagnosisData={diagnosisData} zone={activePattern} isSubmitted={isSubmitted} />
        </div>,
        document.body
      )}

      {/* ── STICKY HEADER (outside container so it spans full width) ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 200, backgroundColor: 'rgba(241,245,249,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${theme.border}` }}>
        <div className="neuro-content-pad" style={{ maxWidth: '1040px', margin: '0 auto', padding: '0 20px' }}>

          {/* nav row — back | stepper | print */}
          <header style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '56px' }}>
            {/* left: back button */}
            <div style={{ width: '44px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={currentStep === 0 ? () => navigate('/home') : handleBack}
                aria-label="Back"
                style={{ minWidth: '44px', minHeight: '44px', padding: '6px 10px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', cursor: 'pointer', fontWeight: '700', fontSize: '1.1rem', color: theme.textMuted, lineHeight: 1, transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ←
              </button>
            </div>

            {/* centre: stepper */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
              {currentStep > 0 && STEPS.slice(1).map((step, i) => {
                const idx = i + 1;
                const isActive = currentStep === idx;
                const isDone   = currentStep > idx;
                return (
                  <React.Fragment key={step}>
                    {i > 0 && (
                      <div style={{ flex: 1, height: '2px', borderRadius: '99px', background: isDone ? theme.success : isActive ? theme.primary : theme.border, transition: 'background 0.3s' }} />
                    )}
                    <div className="neuro-step-node" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem', fontWeight: '800', transition: 'all 0.25s',
                        background: isDone ? theme.success : isActive ? theme.primary : '#e2e8f0',
                        color: isDone || isActive ? '#fff' : '#94a3b8',
                        boxShadow: isActive ? `0 0 0 3px ${theme.primary}22` : 'none',
                        flexShrink: 0,
                      }}>
                        {isDone ? '✓' : idx}
                      </div>
                      <span className="neuro-step-label" style={{ fontSize: '0.6rem', fontWeight: isActive ? '700' : '500', color: isActive ? theme.primary : isDone ? theme.success : '#94a3b8', whiteSpace: 'nowrap' }}>
                        {step}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
              {currentStep === 0 && activePattern && (
                <span className="neuro-zone-badge" style={{ background: `${CLINICAL_DATABASE[activePattern]?.color}18`, color: CLINICAL_DATABASE[activePattern]?.color, padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '700' }}>
                  {activePattern}
                </span>
              )}
            </div>

          </header>

          {/* print row — only on last step */}
          {currentStep === 4 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px' }}>
              <button type="button" onClick={() => navigate('/home')}
                style={{ padding: '5px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: '#4f46e5', transition: 'background-color 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.borderColor = '#a5b4fc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.border; }}>
                ⌂ Homepage
              </button>
              {isSubmitted && (
                <button type="button" onClick={() => window.print()}
                  style={{ padding: '5px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: '#16a34a', transition: 'background-color 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.border; }}>
                  <span style={{ fontSize: '1rem' }}>↓</span> PDF
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      <div style={styles.container}>

        {currentStep === 0 && (
          <div style={{ animation: 'fadeIn 0.3s' }}>
            <h3 className="neuro-pattern-title" style={{ color: theme.textMuted, textAlign: 'center', marginBottom: '40px', fontWeight: '600', fontSize: '1.2rem' }}>Select Primary Clinical Presentation</h3>
            <div className="neuro-pattern-grid" style={styles.zoneGrid}>
              {Object.entries(CLINICAL_DATABASE).map(([key, cfg]) => (
                <div key={key}
                  className="neuro-pattern-card"
                  style={{ ...styles.zoneCard, ...(hoveredZone === key ? { transform: 'translateY(-6px)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', borderColor: cfg.color } : {}) }}
                  onMouseEnter={() => setHoveredZone(key)} onMouseLeave={() => setHoveredZone('')}
                  onClick={() => { setActivePattern(key); setCurrentStep(1); }}>
                  <div className="neuro-pattern-icon" style={{ width: '60px', height: '60px', backgroundColor: `${cfg.color}15`, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', marginBottom: '15px' }}>{cfg.icon}</div>
                  <span className="neuro-pattern-name" style={{ fontSize: '1.4rem', fontWeight: '800', color: theme.textMain }}>{key}</span>
                  <span className="neuro-pattern-region" style={{ fontSize: '0.95rem', color: theme.textMuted, fontWeight: '500' }}>{cfg.region}</span>
                </div>
              ))}
            </div>

            {/* ── Clinical Reference Guide ── */}
            <div className="neuro-ref-section" style={{ marginTop: '48px' }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <span className="neuro-ref-label" style={{ fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8' }}>Clinical Reference Guide — Pattern Selection</span>
              </div>
              <div className="neuro-ref-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>

                {/* UMN */}
                <div className="neuro-ref-card" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '18px', padding: '20px' }}>
                  <div className="neuro-ref-card-hdr" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div className="neuro-ref-card-icon" style={{ width: '32px', height: '32px', background: '#6366f1', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🧠</div>
                    <div>
                      <div className="neuro-ref-card-title" style={{ fontWeight: '800', fontSize: '0.9rem', color: '#4338ca' }}>UMN Pattern</div>
                      <div className="neuro-ref-card-sub" style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: '600' }}>Upper Motor Neuron</div>
                    </div>
                  </div>
                  {[
                    { group: 'Brain (Cortex / Subcortex)', items: ['Stroke (ischemic / hemorrhagic)', 'Brain tumour', 'Traumatic brain injury (TBI)', 'Cerebral palsy', 'Multiple sclerosis (brain lesions)', 'Brain abscess'] },
                    { group: 'Spinal Cord', items: ['Spinal cord injury (SCI)', 'Cervical / thoracic myelopathy', 'Transverse myelitis', 'Spinal cord tumour', 'Syringomyelia (late stage)'] },
                    { group: 'Neurodegenerative', items: ['ALS (UMN component)', 'Primary lateral sclerosis'] },
                  ].map(({ group, items }) => (
                    <div key={group} className="neuro-ref-group" style={{ marginBottom: '12px' }}>
                      <div className="neuro-ref-group-title" style={{ fontSize: '0.72rem', fontWeight: '800', color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>{group}</div>
                      {items.map(item => (
                        <div key={item} className="neuro-ref-item" style={{ fontSize: '0.8rem', color: '#3730a3', lineHeight: 1.5, paddingLeft: '10px' }}>· {item}</div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* LMN */}
                <div className="neuro-ref-card" style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '18px', padding: '20px' }}>
                  <div className="neuro-ref-card-hdr" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div className="neuro-ref-card-icon" style={{ width: '32px', height: '32px', background: '#10b981', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>⚡</div>
                    <div>
                      <div className="neuro-ref-card-title" style={{ fontWeight: '800', fontSize: '0.9rem', color: '#065f46' }}>LMN Pattern</div>
                      <div className="neuro-ref-card-sub" style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: '600' }}>Lower Motor Neuron</div>
                    </div>
                  </div>
                  {[
                    { group: 'Anterior Horn Cell', items: ['Polio', 'ALS (LMN component)', 'Spinal muscular atrophy'] },
                    { group: 'Nerve Root (Radiculopathy)', items: ['Disc prolapse', 'Nerve root compression'] },
                    { group: 'Peripheral Nerve', items: ['Peripheral neuropathy (diabetic, alcohol etc.)', 'Guillain-Barré syndrome', 'Nerve injury (trauma, compression)', 'Entrapment (carpal tunnel, ulnar nerve)'] },
                    { group: 'Neuromuscular Junction', items: ['Myasthenia gravis', 'Lambert-Eaton syndrome'] },
                    { group: 'Muscle', items: ['Muscular dystrophy', 'Myopathy (inflammatory, metabolic)'] },
                  ].map(({ group, items }) => (
                    <div key={group} className="neuro-ref-group" style={{ marginBottom: '12px' }}>
                      <div className="neuro-ref-group-title" style={{ fontSize: '0.72rem', fontWeight: '800', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>{group}</div>
                      {items.map(item => (
                        <div key={item} className="neuro-ref-item" style={{ fontSize: '0.8rem', color: '#064e3b', lineHeight: 1.5, paddingLeft: '10px' }}>· {item}</div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Cerebellar */}
                <div className="neuro-ref-card" style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '18px', padding: '20px' }}>
                  <div className="neuro-ref-card-hdr" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div className="neuro-ref-card-icon" style={{ width: '32px', height: '32px', background: '#8b5cf6', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🎯</div>
                    <div>
                      <div className="neuro-ref-card-title" style={{ fontWeight: '800', fontSize: '0.9rem', color: '#5b21b6' }}>Cerebellar Pattern</div>
                      <div className="neuro-ref-card-sub" style={{ fontSize: '0.72rem', color: '#8b5cf6', fontWeight: '600' }}>Coordination & Balance</div>
                    </div>
                  </div>
                  <div className="neuro-ref-group" style={{ marginBottom: '8px' }}>
                    <div className="neuro-ref-group-title" style={{ fontSize: '0.72rem', fontWeight: '800', color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Causes</div>
                    {['Cerebellar stroke', 'Cerebellar tumour', 'Alcoholic cerebellar degeneration', 'Multiple sclerosis (cerebellar involvement)', 'Spinocerebellar ataxia', 'Drug toxicity (phenytoin etc.)'].map(item => (
                      <div key={item} className="neuro-ref-item" style={{ fontSize: '0.8rem', color: '#4c1d95', lineHeight: 1.5, paddingLeft: '10px' }}>· {item}</div>
                    ))}
                  </div>
                </div>

                {/* Sensory */}
                <div className="neuro-ref-card" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '18px', padding: '20px' }}>
                  <div className="neuro-ref-card-hdr" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div className="neuro-ref-card-icon" style={{ width: '32px', height: '32px', background: '#f59e0b', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🌡️</div>
                    <div>
                      <div className="neuro-ref-card-title" style={{ fontWeight: '800', fontSize: '0.9rem', color: '#92400e' }}>Sensory Pattern</div>
                      <div className="neuro-ref-card-sub" style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: '600' }}>Sensory Loss / Disturbance</div>
                    </div>
                  </div>
                  {[
                    { group: 'Peripheral', items: ['Diabetic neuropathy', 'Vitamin deficiency (B12)', 'Toxic neuropathy'] },
                    { group: 'Spinal Cord', items: ['Sensory level (SCI, myelopathy)', 'Tabes dorsalis'] },
                    { group: 'Brain', items: ['Thalamic stroke', 'Parietal lobe lesion'] },
                  ].map(({ group, items }) => (
                    <div key={group} className="neuro-ref-group" style={{ marginBottom: '12px' }}>
                      <div className="neuro-ref-group-title" style={{ fontSize: '0.72rem', fontWeight: '800', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>{group}</div>
                      {items.map(item => (
                        <div key={item} className="neuro-ref-item" style={{ fontSize: '0.8rem', color: '#78350f', lineHeight: 1.5, paddingLeft: '10px' }}>· {item}</div>
                      ))}
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div style={styles.sectionCard}>

            {/* ── Section header ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px', paddingBottom: '18px', borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: 'linear-gradient(135deg,#4f46e5,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>👤</div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800', color: theme.textMain, letterSpacing: '-0.02em' }}>Patient Identification</h3>
                <span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600' }}>Enter patient demographics and registration details</span>
              </div>
            </div>

            <div style={styles.gridContainer}>
              {/* Full Name */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Full Name <span style={{ color: theme.danger }}>*</span></label>
                <input
                  style={styles.input}
                  value={formData.name} name="name"
                  onChange={handleChange}
                  placeholder="e.g. Jane Doe"
                  onFocus={e => e.target.style.borderColor = theme.primary}
                  onBlur={e => e.target.style.borderColor = theme.border}
                />
              </div>
              {/* Age */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Age</label>
                <input
                  type="number"
                  style={styles.input}
                  value={formData.age} name="age"
                  onChange={handleChange}
                  placeholder="—"
                  onFocus={e => e.target.style.borderColor = theme.primary}
                  onBlur={e => e.target.style.borderColor = theme.border}
                />
              </div>
              {/* Gender */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Gender</label>
                <select style={styles.input} name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              {/* Occupation */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Occupation</label>
                <input
                  style={styles.input}
                  value={formData.occupation} name="occupation"
                  onChange={handleChange}
                  placeholder="e.g. Teacher"
                  onFocus={e => e.target.style.borderColor = theme.primary}
                  onBlur={e => e.target.style.borderColor = theme.border}
                />
              </div>
              {/* Dominant Hand */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Dominant Hand</label>
                <select style={styles.input} name="dominantHand" value={formData.dominantHand} onChange={handleChange}>
                  <option>Right</option>
                  <option>Left</option>
                  <option>Ambidextrous</option>
                </select>
              </div>
              {/* Patient Type */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Patient Type</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[{ val: 'OP', label: 'Outpatient' }, { val: 'IP', label: 'Inpatient' }].map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => handleChange({ target: { name: 'ipOp', value: opt.val } })}
                      style={{
                        flex: 1, padding: '14px 8px', borderRadius: '12px', border: `1.5px solid ${formData.ipOp === opt.val ? theme.primary : theme.border}`,
                        background: formData.ipOp === opt.val ? '#eef2ff' : '#f8fafc',
                        color: formData.ipOp === opt.val ? theme.primary : theme.textMuted,
                        fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.15s', minHeight: 52
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Registration No */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Registration No.</label>
                <input
                  style={styles.input}
                  value={formData.regNo} name="regNo"
                  onChange={handleChange}
                  placeholder="Hospital ID"
                  onFocus={e => e.target.style.borderColor = theme.primary}
                  onBlur={e => e.target.style.borderColor = theme.border}
                />
              </div>
              {/* Date */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Assessment Date</label>
                <input
                  type="date"
                  style={styles.input}
                  value={formData.date} name="date"
                  onChange={handleChange}
                  onFocus={e => e.target.style.borderColor = theme.primary}
                  onBlur={e => e.target.style.borderColor = theme.border}
                />
              </div>
              {/* Medical Diagnosis */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Medical Diagnosis</label>
                <input
                  style={styles.input}
                  value={formData.diagnosis} name="diagnosis"
                  onChange={handleChange}
                  placeholder="e.g. Stroke, Cerebral Palsy, SCI..."
                  onFocus={e => e.target.style.borderColor = theme.primary}
                  onBlur={e => e.target.style.borderColor = theme.border}
                />
              </div>
            </div>

          </div>
        )}

        {currentStep === 2 && activePattern && SUBJECTIVE_DB[activePattern] && (() => {
          const sdb = SUBJECTIVE_DB[activePattern];
          const patternColor = sdb.color;
          const totalFindings = sdb.sections.reduce((acc, sec) => {
            return acc + sec.fields.filter(f => {
              const v = formData[f.key];
              return Array.isArray(v) ? v.some(o => !isNormalOpt(o)) : (v && !isNormalOpt(v));
            }).length;
          }, 0);

          return (
            <div style={styles.sectionCard}>
              {/* Header */}
              <div className="neuro-subj-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: `2px solid ${sdb.border}` }}>
                <div>
                  <h3 className="neuro-subj-title" style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: theme.textMain }}>💬 Subjective Assessment</h3>
                  <span style={{ fontSize: '0.85rem', color: theme.textMuted, fontWeight: '600' }}>Patient-reported history — {activePattern}</span>
                </div>
                <div className="neuro-subj-badges" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {totalFindings > 0 && (
                    <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '20px', padding: '4px 14px', fontSize: '0.8rem', fontWeight: '800' }}>
                      {totalFindings} finding{totalFindings > 1 ? 's' : ''} noted
                    </span>
                  )}
                  <span style={{ backgroundColor: `${patternColor}18`, color: patternColor, padding: '6px 14px', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem' }}>
                    {CLINICAL_DATABASE[activePattern].icon} {activePattern}
                  </span>
                </div>
              </div>

              {/* HPI + Onset row */}
              <div className="neuro-subj-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                {/* Onset */}
                <div style={{ background: `${patternColor}08`, borderRadius: '14px', padding: '16px', border: `1px solid ${sdb.border}` }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '800', color: patternColor, marginBottom: '10px' }}>📅 Onset & Duration</div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>Onset Type</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {sdb.onsetTypes.map(opt => {
                        const active = formData.symOnset === opt;
                        return (
                          <button key={opt} type="button" onClick={() => handleChange({ target: { name: 'symOnset', value: active ? '' : opt } })} style={{
                            padding: '4px 11px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                            border: `1.5px solid ${active ? patternColor : '#e2e8f0'}`,
                            background: active ? `${patternColor}18` : '#f8fafc',
                            color: active ? patternColor : '#64748b'
                          }}>{opt}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>Body Side / Distribution</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {sdb.sideOptions.map(opt => {
                        const active = formData.symSide === opt;
                        return (
                          <button key={opt} type="button" onClick={() => handleChange({ target: { name: 'symSide', value: active ? '' : opt } })} style={{
                            padding: '4px 11px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                            border: `1.5px solid ${active ? patternColor : '#e2e8f0'}`,
                            background: active ? `${patternColor}18` : '#f8fafc',
                            color: active ? patternColor : '#64748b'
                          }}>{opt}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>Duration of symptoms</div>
                    <input type="text" placeholder="e.g. 3 months, 6 weeks..." value={formData.symDuration || ''} onChange={(e) => handleChange({ target: { name: 'symDuration', value: e.target.value } })}
                      style={{ ...styles.input, fontSize: '0.85rem', padding: '8px 12px' }} />
                  </div>
                </div>

                {/* Cause + VAS */}
                <div style={{ background: `${patternColor}08`, borderRadius: '14px', padding: '16px', border: `1px solid ${sdb.border}` }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '800', color: patternColor, marginBottom: '10px' }}>🏷️ Probable Cause / Diagnosis</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
                    {sdb.causes.map(opt => {
                      const curr = formData.symCause || [];
                      const active = curr.includes(opt);
                      return (
                        <button key={opt} type="button" onClick={() => {
                          const next = active ? curr.filter(o => o !== opt) : [...curr, opt];
                          handleChange({ target: { name: 'symCause', value: next } });
                        }} style={{
                          padding: '4px 11px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                          border: `1.5px solid ${active ? patternColor : '#e2e8f0'}`,
                          background: active ? `${patternColor}18` : '#f8fafc',
                          color: active ? patternColor : '#64748b'
                        }}>{opt}</button>
                      );
                    })}
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b' }}>Symptom Severity (VAS)</span>
                      <span style={{ fontWeight: '800', fontSize: '1rem', color: parseInt(formData.symSeverity) >= 7 ? '#dc2626' : patternColor }}>{formData.symSeverity || 0}/10</span>
                    </div>
                    <input type="range" min="0" max="10" style={{ width: '100%', cursor: 'pointer', accentColor: patternColor }} value={formData.symSeverity || 0} onChange={(e) => handleChange({ target: { name: 'symSeverity', value: e.target.value } })} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                      <span>No pain</span><span>Worst imaginable</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chief Complaint */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...styles.label, display: 'block', marginBottom: '8px' }}>🗣 Chief Complaint</label>
                <input
                  type="text"
                  style={{ ...styles.input }}
                  value={formData.chiefComplaint || ''}
                  onChange={(e) => handleChange({ target: { name: 'chiefComplaint', value: e.target.value } })}
                  placeholder={`e.g. Weakness in right arm since 2 weeks...`}
                />
              </div>

              {/* HPI */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...styles.label, display: 'block', marginBottom: '8px' }}>📝 History of Present Illness (HPI)</label>
                <textarea
                  style={{ ...styles.input, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                  value={formData.hpiText || ''}
                  onChange={(e) => handleChange({ target: { name: 'hpiText', value: e.target.value } })}
                  placeholder={`Describe the onset, progression and nature of ${activePattern.toLowerCase()} symptoms in the patient's own words...`}
                />
              </div>

              {/* Pattern-specific collapsible sections */}
              {sdb.sections.map(section => (
                <SubjectiveSection
                  key={section.key}
                  section={section}
                  formData={formData}
                  onChange={(key, val) => setFormData(prev => ({ ...prev, [key]: val }))}
                  patternColor={patternColor}
                />
              ))}
            </div>
          );
        })()}

        {currentStep === 3 && activePattern && CLINICAL_DATABASE[activePattern] && (
          <div style={styles.sectionCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
              <h3 style={{ ...styles.sectionTitle, borderBottom: 'none', padding: 0, margin: 0 }}>🩺 Clinical Examination</h3>
              <span style={{ backgroundColor: `${CLINICAL_DATABASE[activePattern].color}15`, color: CLINICAL_DATABASE[activePattern].color, padding: '8px 16px', borderRadius: '12px', fontWeight: '800', fontSize: '0.9rem', textTransform: 'uppercase' }}>{activePattern} Assessment</span>
            </div>

            {(() => {
              const NEURO_SECTION_GROUP = {
                'Observation':'Motor & Physical','Palpation':'Motor & Physical','Muscle Girth':'Motor & Physical',
                'Involuntary Movements':'Motor & Physical','Motor System':'Motor & Physical','ROM':'Motor & Physical',
                'Sensory System':'Sensory & Cranial','Sensory Testing (Core)':'Sensory & Cranial',
                'Cranial Nerves':'Sensory & Cranial','Reflexes':'Sensory & Cranial',
                'Coordination & Balance':'Functional','Coordination (Core)':'Functional','Coordination':'Functional',
                'Balance':'Functional','Equilibrium':'Functional','Posture':'Functional',
                'Gait Analysis (Detailed)':'Functional','Gait Analysis':'Functional','Hand Function':'Functional',
                'Cognition (MMSE)':'Cognitive & Systems','Functional Assessment':'Cognitive & Systems','Systems Review':'Cognitive & Systems',
              };
              const GROUP_META = {
                'Motor & Physical':   { color:'#1d4ed8', bg:'#eff6ff', border:'#bfdbfe', icon:'💪' },
                'Sensory & Cranial':  { color:'#7c3aed', bg:'#f5f3ff', border:'#ddd6fe', icon:'🧠' },
                'Functional':         { color:'#059669', bg:'#f0fdf4', border:'#a7f3d0', icon:'🏃' },
                'Cognitive & Systems':{ color:'#d97706', bg:'#fffbeb', border:'#fde68a', icon:'🧩' },
              };
              let lastGroup = null;
              return CLINICAL_DATABASE[activePattern].sections.flatMap((section) => {
                const group = NEURO_SECTION_GROUP[section.title];
                const gm = group ? GROUP_META[group] : null;
                const elements = [];
                if (group && group !== lastGroup && gm) {
                  lastGroup = group;
                  elements.push(
                    <div key={`grp-${group}`} style={{ display:'flex', alignItems:'center', gap:8, margin:'20px 0 10px', paddingBottom:8, borderBottom:`2px solid ${gm.border}` }}>
                      <span style={{ fontSize:15 }}>{gm.icon}</span>
                      <span style={{ fontSize:'0.8rem', fontWeight:800, color:gm.color, textTransform:'uppercase', letterSpacing:'0.06em' }}>{group}</span>
                    </div>
                  );
                } else if (group) {
                  lastGroup = group;
                }

              // Observation and Palpation → fully collapsible pill component
              if (section.title === 'Observation' || section.title === 'Palpation' || section.title === 'Involuntary Movements' || section.title === 'Muscle Girth' || section.title === 'Sensory Testing (Core)' || section.title === 'Equilibrium') {
                const patternColor = CLINICAL_DATABASE[activePattern].color;
                // Only use fields that are multi or single (skip special types)
                const sectionFields = section.fields.filter(f => f.type === 'multi' || f.type === 'single');
                elements.push(
                  <div key={section.title} style={{ marginBottom: '16px' }}>
                    <CollapsibleSectionAssessment
                      sectionTitle={section.title}
                      fields={sectionFields}
                      value={formData}
                      onFieldChange={(key, val) => setFormData(prev => ({ ...prev, [key]: val }))}
                      patternColor={patternColor}
                    />
                  </div>
                );
                return elements;
              }

              elements.push(
              <div key={section.title} style={{ marginBottom: '24px' }}>
                <h4 style={styles.subBoxTitle}>{section.title}</h4>
                <div style={styles.gridContainer}>
                  {section.fields.map((field) => {

                    if (field.key === 'mmt') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <ExpandableMuscleTone
                            label="MMT Grading Table"
                            tab="mmt"
                            color="#4f46e5"
                            dashColor="#a5b4fc"
                            bgColor="#eef2ff"
                            state={formData.mmtState || {}}
                            onSelect={(partial) => setFormData(prev => ({
                              ...prev,
                              mmtState: { ...prev.mmtState, ...partial }
                            }))}
                            onClear={() => setFormData(prev => ({ ...prev, mmtState: {} }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'cranial') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <CranialNerveAssessment
                            value={formData.cranialData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, cranialData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'sensory') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <SensoryAssessment
                            value={formData.sensoryData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, sensoryData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.key === 'tone') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <ExpandableMuscleTone
                            label="Muscle Tone (Spasticity) Table"
                            tab="tone"
                            color="#8b5cf6"
                            dashColor="#c4b5fd"
                            bgColor="#faf5ff"
                            state={formData.toneState || {}}
                            onSelect={(partial) => setFormData(prev => ({
                              ...prev,
                              toneState: { ...prev.toneState, ...partial }
                            }))}
                            onClear={() => setFormData(prev => ({ ...prev, toneState: {} }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'mmse') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <MMSEScorer
                            value={formData.mmseData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, mmseData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'fim') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <FIMAssessment
                            value={formData.fimData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, fimData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'voluntary') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <VoluntaryControlAssessment
                            value={formData.voluntaryControlData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, voluntaryControlData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'rom') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <ROMAssessment
                            value={formData.romData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, romData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'reflex') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <ReflexAssessment
                            value={formData.reflexData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, reflexData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'coordination') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <CoordinationAssessment
                            value={formData.coordinationData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, coordinationData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'balance') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <BalanceAssessment
                            value={formData.balanceData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, balanceData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'posture') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <PostureAssessment
                            value={formData.postureData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, postureData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'gait') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <GaitAnalysisAssessment
                            value={formData.gaitData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, gaitData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'hand') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <HandFunctionAssessment
                            value={formData.handData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, handData: val }))}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'systems') {
                      return (
                        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                          <SystemsReviewAssessment
                            value={formData.systemsData || {}}
                            onChange={(val) => setFormData(prev => ({ ...prev, systemsData: val }))}
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={field.key}>
                        <label style={{ ...styles.label, display: 'block', marginBottom: '10px' }}>{field.label}</label>
                        {field.type === "multi" ? (
                          <div style={styles.chipRow}>
                            {field.options?.map(opt => {
                              const isActive = formData[field.key]?.includes(opt);
                              return (
                                <button key={opt} type="button"
                                  style={{ ...styles.chip, ...(isActive ? { ...styles.chipActive, backgroundColor: CLINICAL_DATABASE[activePattern].color, borderColor: CLINICAL_DATABASE[activePattern].color, boxShadow: `0 4px 12px ${CLINICAL_DATABASE[activePattern].color}40` } : {}) }}
                                  onClick={() => toggleChip(field.key, opt)}>
                                  {isActive ? '✓ ' : '+ '} {opt}
                                </button>
                              );
                            })}
                          </div>
                        ) : field.type === "single" ? (
                          <select style={styles.input} value={formData[field.key] || ''} onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}>
                            <option value="">Select...</option>
                            {field.options?.map(o => <option key={o}>{o}</option>)}
                          </select>
                        ) : field.type === "number" ? (
                          <input type="number" style={styles.input} value={formData[field.key] || ''} onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })} />
                        ) : field.type === "textarea" ? (
                          <textarea style={{ ...styles.input, ...styles.textarea }} value={formData[field.key] || ''} onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
              return elements;
            });
            })()}

            {/* Investigation */}
            <div style={{ marginTop: '30px', borderTop: `2px solid ${theme.border}`, paddingTop: '24px' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: theme.textMain, marginBottom: '16px' }}>🩻 Investigation</h4>
              {formData.investigation_list?.map(row => <SmartRow key={row.id} mode="INVESTIGATION" data={row} onChange={(u) => updateRow('investigation_list', row.id, u)} onDelete={() => deleteRow('investigation_list', row.id)} />)}
              <button type="button" style={{ ...styles.addBtn, color: CLINICAL_DATABASE[activePattern]?.color || theme.primary, borderColor: `${CLINICAL_DATABASE[activePattern]?.color || theme.primary}40`, backgroundColor: `${CLINICAL_DATABASE[activePattern]?.color || theme.primary}05` }} onClick={() => addRow('investigation_list', { type: '', finding: '' })}>+ Add Neuro Imaging / Lab Result</button>
            </div>
          </div>
        )}
        {/* Clinical Impression */}
        {currentStep === 4 && (
          <div style={styles.sectionCard}>

            {/* ── Header + Tab Switch ── */}
            <div style={{ borderBottom: `2px solid ${theme.secondary}`, paddingBottom: '20px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: theme.textMain, margin: '0 0 16px' }}>🧠 Clinical Impression</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[['ai', '🧠 Analyze with AI'], ['manual', '✍️ Manual Entry']].map(([tab, label]) => (
                  <button key={tab} type="button" onClick={() => setDiagTab(tab)} style={{
                    padding: '10px 20px', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem',
                    cursor: 'pointer', border: 'none', transition: 'background-color 0.15s, color 0.15s, box-shadow 0.15s',
                    background: diagTab === tab ? theme.primary : '#f1f5f9',
                    color: diagTab === tab ? '#fff' : theme.textMuted,
                    boxShadow: diagTab === tab ? '0 2px 8px rgba(79,70,229,0.25)' : 'none',
                  }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ══ AI TAB ══ */}
            {diagTab === 'ai' && (
              <div>
                {!isSubmitted ? (
                  <div className="ai-card-pre">
                    <h4 className="ai-pre-title">Objective Data Collection Complete</h4>
                    <p className="ai-pre-desc">Run the AI Pattern Analyzer to identify lesion localization, impairment distribution, and rehab priorities based on the collected clinical findings.</p>
                    <button type="button" onClick={handleFinalSubmit} disabled={isDiagnosing} className="ai-analyze-btn"
                      style={{ background: isDiagnosing ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: '#fff', border: 'none', borderRadius: '12px', cursor: isDiagnosing ? 'not-allowed' : 'pointer', fontWeight: '800', boxShadow: isDiagnosing ? 'none' : '0 10px 25px -5px rgba(79,70,229,0.4)', transition: 'all 0.2s' }}
                      onMouseEnter={(e) => !isDiagnosing && (e.currentTarget.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => !isDiagnosing && (e.currentTarget.style.transform = 'translateY(0)')}>
                      {isDiagnosing ? 'Analyzing Neurological Pattern...' : 'Analyze Neurological Pattern ✨'}
                    </button>
                  </div>
                ) : (
                  <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                      <div className="ai-results-header">
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: theme.textMain, margin: 0 }}>🧠 Neurological Pattern Analysis</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ background: '#eff6ff', color: '#4f46e5', padding: '8px 16px', borderRadius: '99px', fontSize: '0.85rem', fontWeight: '800', border: '1px solid #c7d2fe' }}>{activePattern}</span>
                          <button
                            type="button"
                            onClick={handleFinalSubmit}
                            disabled={isDiagnosing}
                            style={{ padding: '8px 16px', background: isDiagnosing ? '#94a3b8' : '#f1f5f9', color: isDiagnosing ? '#fff' : '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '99px', cursor: isDiagnosing ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.82rem', transition: 'all 0.2s' }}
                          >
                            {isDiagnosing ? 'Running…' : '↺ Re-run'}
                          </button>
                        </div>
                      </div>

                      {/* AI Disclaimer */}
                      <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '10px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#92400e', lineHeight: 1.55 }}>
                          <strong>AI-generated content — clinical review required.</strong> This analysis is produced by an AI model and may contain errors or omissions. It is intended to support, not replace, clinical judgement. Always verify findings against the patient's full clinical picture before acting.
                        </p>
                      </div>

                      {/* Doctor-given diagnosis */}
                      {formData.diagnosis && (
                        <div style={{ marginBottom: '25px', padding: '18px 20px', borderRadius: '14px', backgroundColor: '#eff6ff', border: '2px solid #bfdbfe' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Medical Diagnosis (Doctor-Given)</div>
                          <p style={{ fontWeight: '700', fontSize: '1.15rem', color: '#1e40af', margin: 0 }}>{formData.diagnosis}</p>
                        </div>
                      )}

                      {/* Lesion Localization */}
                      {diagnosisData.lesionLocalization && (
                        <div style={{ marginBottom: '20px', padding: '18px 20px', borderRadius: '14px', backgroundColor: '#faf5ff', border: '1px solid #e9d5ff' }}>
                          <h4 style={{ color: '#7c3aed', marginTop: 0, fontSize: '1rem', fontWeight: '800', marginBottom: '8px' }}>📍 Lesion Localization</h4>
                          <p style={{ color: theme.textMain, margin: 0, lineHeight: 1.6 }}>{diagnosisData.lesionLocalization}</p>
                        </div>
                      )}

                      {/* UMN/LMN + Distribution */}
                      {(diagnosisData.umnLmnClassification || diagnosisData.impairmentDistribution) && (
                        <div className="ai-grid-2">
                          {diagnosisData.umnLmnClassification && (
                            <div style={{ padding: '18px 20px', borderRadius: '14px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
                              <h4 style={{ color: '#c2410c', marginTop: 0, fontSize: '0.95rem', fontWeight: '800', marginBottom: '8px' }}>⚡ UMN / LMN Classification</h4>
                              <p style={{ color: theme.textMain, margin: 0, fontWeight: '600', lineHeight: 1.5 }}>{diagnosisData.umnLmnClassification}</p>
                            </div>
                          )}
                          {diagnosisData.impairmentDistribution && (
                            <div style={{ padding: '18px 20px', borderRadius: '14px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                              <h4 style={{ color: '#15803d', marginTop: 0, fontSize: '0.95rem', fontWeight: '800', marginBottom: '8px' }}>🗺 Impairment Distribution</h4>
                              <p style={{ color: theme.textMain, margin: 0, fontWeight: '600', lineHeight: 1.5 }}>{diagnosisData.impairmentDistribution}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Functional Consequences */}
                      {diagnosisData.functionalConsequences?.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ marginTop: 0, color: theme.textMain, fontSize: '1.1rem', fontWeight: '800', marginBottom: '12px' }}>🚶 Functional Consequences</h4>
                          <ul style={{ paddingLeft: '22px', marginBottom: 0, lineHeight: 1.7, color: theme.textMuted, fontSize: '0.97rem' }}>
                            {diagnosisData.functionalConsequences.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Prognosis Timeline inline with lesion (appended above) */}
                      {diagnosisData.prognosisTimeline && (
                        <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '12px', backgroundColor: '#f0fdf4', border: '1px solid #6ee7b7' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.06em' }}>📅 Prognosis Timeline — </span>
                          <span style={{ fontSize: '0.88rem', color: '#065f46', fontStyle: 'italic' }}>{diagnosisData.prognosisTimeline}</span>
                        </div>
                      )}

                      {/* Rehab Priorities — structured cards */}
                      {diagnosisData.rehabPriorities?.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ marginTop: 0, color: theme.textMain, fontSize: '1.1rem', fontWeight: '800', marginBottom: '12px' }}>🎯 Rehab Priorities</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {diagnosisData.rehabPriorities.map((item, i) => {
                              const pColor = i === 0 ? '#ef4444' : i === 1 ? '#f59e0b' : i === 2 ? '#3b82f6' : '#10b981';
                              const isString = typeof item === 'string';
                              return (
                                <div key={i} style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '14px 16px', borderLeft: `4px solid ${pColor}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', backgroundColor: pColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '0.82rem', flexShrink: 0 }}>{i + 1}</div>
                                  <div style={{ flex: 1 }}>
                                    {isString ? (
                                      <div style={{ fontSize: '0.9rem', color: theme.textMain, lineHeight: 1.5 }}>{item}</div>
                                    ) : (
                                      <>
                                        {item.priority && <div style={{ fontWeight: '800', fontSize: '0.9rem', color: theme.textMain, marginBottom: '4px' }}>{item.priority}</div>}
                                        {item.intervention && <div style={{ fontSize: '0.83rem', color: theme.textMuted, marginBottom: '4px', lineHeight: 1.5 }}>{item.intervention}</div>}
                                        {item.frequency && <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '4px' }}>⏱ {item.frequency}</div>}
                                        {item.goal && <div style={{ fontSize: '0.82rem', color: '#4f46e5', fontWeight: '600' }}>🎯 {item.goal}</div>}
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Short-term + Long-term Goals */}
                      {(diagnosisData.shortTermGoals?.length > 0 || diagnosisData.longTermGoals?.length > 0) && (
                        <div className="ai-grid-2">
                          {diagnosisData.shortTermGoals?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#faf5ff', border: '1px solid #e9d5ff' }}>
                              <h4 style={{ marginTop: 0, color: '#7c3aed', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🎯 Short-Term Goals <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#9d74d8' }}>(2–4 wks)</span></h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.shortTermGoals.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          {diagnosisData.longTermGoals?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                              <h4 style={{ marginTop: 0, color: '#1d4ed8', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🏁 Long-Term Goals <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#3b82f6' }}>(3–6 mo)</span></h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.longTermGoals.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Treatment Precautions */}
                      {diagnosisData.treatmentPrecautions?.length > 0 && (
                        <div style={{ padding: '18px 20px', borderRadius: '14px', backgroundColor: '#fef2f2', border: '2px solid #fca5a5', marginBottom: '20px' }}>
                          <h4 style={{ color: '#991b1b', marginTop: 0, fontSize: '1rem', fontWeight: '800', marginBottom: '10px' }}>🚫 Treatment Precautions</h4>
                          <ul style={{ color: '#7f1d1d', paddingLeft: '20px', margin: 0, lineHeight: 1.7, fontSize: '0.88rem' }}>
                            {diagnosisData.treatmentPrecautions.map((item, i) => <li key={i}>{item}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Assistive Devices + Discharge Goals */}
                      {(diagnosisData.assistiveDeviceRecommendations?.length > 0 || diagnosisData.dischargeGoals?.length > 0) && (
                        <div className="ai-grid-2">
                          {diagnosisData.assistiveDeviceRecommendations?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#f8fafc', border: `1px solid ${theme.border}` }}>
                              <h4 style={{ marginTop: 0, color: theme.textMain, fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🦽 Assistive Devices</h4>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {diagnosisData.assistiveDeviceRecommendations.map((item, i) => (
                                  <span key={i} style={{ fontSize: '0.82rem', fontWeight: '700', padding: '4px 12px', borderRadius: '99px', background: '#e0e7ff', color: '#3730a3' }}>{item}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {diagnosisData.dischargeGoals?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                              <h4 style={{ marginTop: 0, color: '#15803d', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🏠 Discharge Goals</h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.dischargeGoals.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Prognosis Indicators */}
                      {diagnosisData.prognosisIndicators?.length > 0 && (
                        <div style={{ marginBottom: '20px', padding: '16px 20px', borderRadius: '14px', backgroundColor: '#fefce8', border: '1px solid #fde68a' }}>
                          <h4 style={{ marginTop: 0, color: '#92400e', fontSize: '1rem', fontWeight: '800', marginBottom: '10px' }}>📈 Prognosis Indicators</h4>
                          <ul style={{ paddingLeft: '20px', margin: 0, color: '#92400e', fontSize: '0.88rem', lineHeight: 1.7 }}>
                            {diagnosisData.prognosisIndicators.map((item, i) => <li key={i}>{item}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Clinical Reasoning */}
                      {diagnosisData.clinicalReasoning?.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ marginTop: 0, color: theme.textMain, fontSize: '1.1rem', fontWeight: '800', marginBottom: '12px' }}>🧩 Clinical Reasoning</h4>
                          <ul style={{ paddingLeft: '22px', marginBottom: 0, lineHeight: 1.7, color: theme.textMuted, fontSize: '0.97rem' }}>
                            {diagnosisData.clinicalReasoning.map((point, i) => <li key={i} style={{ marginBottom: '8px' }}>{point}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Chat Interface */}
                      <div style={{ marginTop: '30px', borderTop: `1px solid ${theme.border}`, paddingTop: '25px' }}>
                        <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: theme.textMain, marginBottom: '15px' }}>💬 Ask AI about this Neurological Pattern</h4>
                        <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', minHeight: '100px', marginBottom: '15px', border: `1px solid ${theme.border}`, maxHeight: '300px', overflowY: 'auto' }}>
                          {chatHistory.map((msg, i) => (
                            <div key={i} style={{ marginBottom: '16px', lineHeight: 1.7, fontSize: '0.95rem' }}>
                              <strong style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: msg.role === 'user' ? '#4f46e5' : '#059669' }}>{msg.role === 'user' ? 'You' : 'NeuroAI'}</strong>
                              {msg.role === 'user' ? (
                                <div style={{ background: '#eff6ff', padding: '12px 16px', borderRadius: '0 12px 12px 12px', display: 'inline-block', color: theme.textMain }}>{msg.text}</div>
                              ) : (
                                <div style={{ marginTop: '4px', color: theme.textMain }}>{renderAiNotes(msg.text)}</div>
                              )}
                            </div>
                          ))}
                          {chatHistory.length === 0 && <p style={{ color: theme.textMuted, fontStyle: 'italic', margin: 0 }}>Ask about tone management, neuroplasticity, functional goals, or prognosis...</p>}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <input
                            style={{ ...styles.input, backgroundColor: '#fff' }}
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="e.g. What functional outcomes are expected for this distribution?"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleChatQuery(); } }}
                          />
                          <button type="button" onClick={handleChatQuery} disabled={chatLoading || !chatInput.trim()}
                            style={{ ...styles.navBtn, ...styles.btnPrimary, padding: '14px 24px', opacity: chatLoading || !chatInput.trim() ? 0.7 : 1, cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer' }}>
                            {chatLoading ? 'Asking...' : 'Ask AI'}
                          </button>
                        </div>
                      </div>

                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px', flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => window.print()}
                        style={{ padding: '14px 28px', background: theme.slate, color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '1.05rem', boxShadow: '0 10px 20px -5px rgba(15,23,42,0.4)', transition: 'all 0.2s', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                        ⬇ Download PDF Report
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══ MANUAL TAB ══ */}
            {diagTab === 'manual' && (
              <div>
                {aiAutoFilled && (
                  <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '12px', padding: '10px 16px', marginBottom: '24px', fontSize: '0.85rem', color: '#065f46', fontWeight: '600' }}>
                    ✅ AI has pre-filled the clinical impression. You can edit it below.
                  </div>
                )}

                {/* Problem List */}
                <div style={{ marginBottom: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: theme.textMain }}>▪ Problem List</h4>
                    <button type="button" onClick={() => addRow('problem_list', { impairment: '', functionalLimitation: '', disability: '' })}
                      style={{ ...styles.addBtn, margin: 0, padding: '6px 14px', fontSize: '0.8rem' }}>+ Add Row</button>
                  </div>
                  <div style={{ border: `1px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden', overflowX: 'auto' }}>
                    <div className="neuro-prob-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#f1f5f9', minWidth: 360 }}>
                      {['IMPAIRMENT', 'FUNCTIONAL LIMITATION', 'DISABILITY'].map(h => (
                        <div key={h} style={{ padding: '10px 14px', fontSize: '0.75rem', fontWeight: '800', color: '#475569', textAlign: 'center', borderBottom: `1px solid ${theme.border}`, borderRight: h !== 'DISABILITY' ? `1px solid ${theme.border}` : 'none', textDecoration: 'underline', letterSpacing: '0.04em' }}>{h}</div>
                      ))}
                    </div>
                    {(formData.problem_list?.length === 0) && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', minWidth: 360, minHeight: '80px' }}>
                        {[0,1,2].map(i => <div key={i} style={{ borderRight: i < 2 ? `1px solid ${theme.border}` : 'none', minHeight: '80px' }} />)}
                      </div>
                    )}
                    {formData.problem_list?.map((row, idx) => (
                      <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', minWidth: 360, borderTop: idx === 0 ? 'none' : `1px solid ${theme.border}`, position: 'relative' }}>
                        {['impairment', 'functionalLimitation', 'disability'].map((field, fi) => (
                          <textarea key={field}
                            value={row[field] || ''}
                            onChange={(e) => updateRow('problem_list', row.id, { [field]: e.target.value })}
                            placeholder="..."
                            style={{ border: 'none', borderRight: fi < 2 ? `1px solid ${theme.border}` : 'none', outline: 'none', resize: 'none', padding: '10px 12px', fontSize: '0.85rem', fontFamily: 'inherit', minHeight: '70px', background: 'transparent', color: theme.textMain, lineHeight: 1.5 }}
                          />
                        ))}
                        <button type="button" onClick={() => deleteRow('problem_list', row.id)}
                          style={{ position: 'absolute', top: '4px', right: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', lineHeight: 1 }}
                          title="Remove row">×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Functional Diagnosis */}
                <div style={{ marginBottom: '32px' }}>
                  <div className="diag-field-row" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <label style={{ fontSize: '1.05rem', fontWeight: '800', color: theme.textMain, whiteSpace: 'nowrap' }}>▪ Functional Diagnosis</label>
                    <input
                      type="text"
                      value={formData.functionalDiagnosis || ''}
                      onChange={(e) => handleChange({ target: { name: 'functionalDiagnosis', value: e.target.value } })}
                      placeholder="e.g. Right hemiplegia with moderate UMN spasticity..."
                      style={{ ...styles.input, flex: 1 }}
                    />
                  </div>
                </div>

                {/* Goals */}
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', fontWeight: '800', color: theme.textMain }}>▪ Goals</h4>
                  <div style={{ border: `1px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                    {[['shortTermGoals', 'Short Term Goals'], ['longTermGoals', 'Long Term Goals']].map(([field, label], idx) => (
                      <div key={field} className="neuro-goals-row" style={{ display: 'grid', gridTemplateColumns: '140px 1fr', borderTop: idx === 0 ? 'none' : `1px solid ${theme.border}` }}>
                        <div style={{ padding: '14px 16px', fontSize: '0.85rem', fontWeight: '700', color: theme.textMain, background: '#f8fafc', borderRight: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', textAlign: 'center', justifyContent: 'center', lineHeight: 1.4 }}>{label}</div>
                        <textarea
                          value={formData[field] || ''}
                          onChange={(e) => handleChange({ target: { name: field, value: e.target.value } })}
                          placeholder={`Enter ${label.toLowerCase()}...`}
                          style={{ border: 'none', outline: 'none', resize: 'none', padding: '12px 14px', fontSize: '0.85rem', fontFamily: 'inherit', minHeight: '70px', background: 'transparent', color: theme.textMain, lineHeight: 1.6 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Treatment Plan */}
                <div style={{ marginBottom: '8px' }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', fontWeight: '800', color: theme.textMain }}>▪ Treatment Plan</h4>
                  <textarea
                    value={formData.treatmentPlan || ''}
                    onChange={(e) => handleChange({ target: { name: 'treatmentPlan', value: e.target.value } })}
                    placeholder="Outline the physiotherapy treatment plan..."
                    style={{ ...styles.input, minHeight: '160px', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

          </div>
        )}

        {currentStep > 0 && (
          <div style={styles.footerBar}>
            <div style={styles.footerInner}>
              {currentStep < 4 && (
                <button type="button" onClick={handleNext} style={styles.footerBtn}>
                  Continue to {STEPS[currentStep + 1]} →
                </button>
              )}
              {currentStep === 4 && (
                <button type="button" onClick={handleSaveAndReturnHome} style={styles.footerBtnSuccess}>
                  Save & Return Home
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>

    {payGate && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: '28px 28px 22px', maxWidth: 380, width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '1.5rem', textAlign: 'center', marginBottom: 10 }}>🔬</div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>Unlock AI Diagnosis</h3>
          <p style={{ margin: '0 0 18px', fontSize: '0.82rem', color: '#64748b', textAlign: 'center', lineHeight: 1.5 }}>
            Your 3 free assessments are used up. Pay <strong>₹19</strong> for this session — unlocks AI diagnosis, refine, chat, and PDF download.
          </p>
          <button
            type="button"
            onClick={() => { setPayGateError(null); payForDiagnosis(payGate.assessmentId, {
              onSuccess: () => { setPayGate(null); handleFinalSubmit(); },
              onCancel:  () => setPayGate(null),
              onError:   (msg) => setPayGateError(msg || 'Payment failed. Please try again.'),
            }); }}
            style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#4f46e5,#3b82f6)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10 }}
          >
            Pay ₹19 with Razorpay
          </button>
          {payGateError && (
            <div style={{ marginBottom: 10, padding: '9px 13px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: '0.76rem', color: '#991b1b', fontWeight: 600 }}>⚠ {payGateError}</div>
          )}
          <button
            type="button"
            onClick={() => { setPayGate(null); setPayGateError(null); }}
            style={{ width: '100%', padding: '11px', background: 'transparent', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default UnifiedNeuroForm;